import React from 'react';

const ErrorModal = ({ 
  isOpen, 
  onClose, 
  error, 
  title = 'Payment Error',
  showRetry = false,
  onRetry = null
}) => {
  if (!isOpen || !error) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-800 rounded-xl shadow-2xl max-w-md w-full border border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-start space-x-3">
              {/* Error Icon */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-900/50 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              {/* Error Message */}
              <div className="flex-1">
                <p className="text-gray-200 text-sm leading-relaxed">
                  {typeof error === 'string' ? error : error.message || 'An unexpected error occurred'}
                </p>
                
                {/* Additional error details if available */}
                {error.squareErrorCode && (
                  <div className="mt-3 p-3 bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-400 font-medium">Error Code:</p>
                    <p className="text-xs text-gray-300">{error.squareErrorCode}</p>
                  </div>
                )}
                
                {/* Troubleshooting Checklist */}
                <div className="mt-4 p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
                  <p className="text-blue-200 text-sm font-medium mb-3">
                    Payment failed; check these common issues before retrying.
                  </p>
                  
                  <div className="mb-3">
                    <p className="text-blue-200 text-xs font-medium mb-2">Quick checklist</p>
                    <ul className="text-gray-300 text-xs space-y-1 list-disc list-inside">
                      <li><strong>Card number and typos:</strong> Verify every digit and retype the number to avoid transposed or missing digits, and confirm saved card data is up to date.</li>
                      <li><strong>Expiration date:</strong> Confirm the card's month and year are current and the card is activated.</li>
                      <li><strong>Billing ZIP code:</strong> Use the card's billing ZIP code exactly as the issuer has on file; this often differs from your shipping or current location ZIP code and must match for address verification.</li>
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-blue-200 text-xs font-medium mb-2">If the charge still fails</p>
                    <ul className="text-gray-300 text-xs space-y-1 list-disc list-inside">
                      <li>Try a different card or payment method.</li>
                      <li>Contact the card issuer to check for fraud holds, blocks, or insufficient funds.</li>
                      <li>Contact support and provide the transaction timestamp, amount, last four digits of the card, and any error text or screenshot to speed investigation.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
            {showRetry && onRetry && (
              <button
                onClick={() => {
                  onClose();
                  onRetry();
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Try Again
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;